import { type FC } from 'react';
import { useForm, useWatch, type FieldErrors } from 'react-hook-form@latest';
import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';

import { _notNil } from '@/littledash';

import Button from '@/components/UI/Button';

interface AddCommentFormData {
  content: string;
}

interface AddCommentFormProps {
  onAddComment: (content: string) => void;
}

const MAX_CHARACTERS = 5000;

const FieldError: FC<{ errors: FieldErrors<AddCommentFormData>; name: keyof AddCommentFormData }> = ({
  errors,
  name,
}) => <ErrorMessage errors={errors} name={name} render={({ message }) => <p className="f6 red db pv1">{message}</p>} />;

const AddCommentForm: FC<AddCommentFormProps> = ({ onAddComment }) => {
  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<AddCommentFormData>();

  const onSubmit = ({ content }: AddCommentFormData) => {
    onAddComment(content);
    reset();
  };

  const content = useWatch({ control, name: 'content' }) ?? '';
  const remainingCharacters = MAX_CHARACTERS - content.length;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mb4"
      data-test-component="AddCommentForm"
      data-test-element="container"
    >
      <div className="mb2">
        <textarea
          id="content"
          style={{ marginBottom: 0, resize: 'vertical' }}
          data-test-element="comment-input"
          placeholder="Add a comment..."
          className={cn({ input__error: _notNil(errors.content) })}
          {...register('content', {
            required: "Comment can't be blank",
            maxLength: {
              value: MAX_CHARACTERS,
              message: `Character limit exceeded +${Math.abs(remainingCharacters)}`,
            },
          })}
        />
        <span className="f7 lh-title fr pt2" data-test-element="character-count">
          {remainingCharacters < 0 ? (
            <>
              Character limit exceeded <strong>+{Math.abs(remainingCharacters)}</strong>
            </>
          ) : (
            <>Characters remaining {remainingCharacters}</>
          )}
        </span>
        <FieldError errors={errors} name="content" />
      </div>
      <Button submit testId="add-comment-button">
        Add comment
      </Button>
    </form>
  );
};

export default AddCommentForm;
